import { useEffect, useRef, useState } from "react";
import { useToast } from "@ir/ui";
import { GetMasterDataRetailerMatchSourceColumnsUseCase } from "@src/application/useCases/masterData";
import { MasterDataRetailerMatchRuleRepository } from "@src/infrastructure/repositories/masterData";
import {
  getMasterDataRetailerMatchSourceColumnsCache,
  setMasterDataRetailerMatchSourceColumnsCache,
} from "@src/presentation/providers/masterData/masterDataListCache";

const inflightSourceColumnsByBrand = new Map<string, Promise<string[]>>();

function loadMatchSourceColumns(brandKey: string): Promise<string[]> {
  const cached = getMasterDataRetailerMatchSourceColumnsCache(brandKey);
  if (cached) {
    return Promise.resolve(cached);
  }

  const inflight = inflightSourceColumnsByBrand.get(brandKey);
  if (inflight) {
    return inflight;
  }

  const request = new GetMasterDataRetailerMatchSourceColumnsUseCase(
    new MasterDataRetailerMatchRuleRepository(),
  )
    .execute()
    .then((columns) => {
      setMasterDataRetailerMatchSourceColumnsCache(brandKey, columns);
      return columns;
    })
    .finally(() => {
      inflightSourceColumnsByBrand.delete(brandKey);
    });

  inflightSourceColumnsByBrand.set(brandKey, request);
  return request;
}

export function useMasterDataRetailerMatchSourceColumns(
  isOpen: boolean,
  brandKey: string,
) {
  const { error: errorToast } = useToast();
  const [localColumns, setLocalColumns] = useState<string[]>([]);
  const [localReady, setLocalReady] = useState(false);
  const errorToastRef = useRef(errorToast);
  errorToastRef.current = errorToast;

  const cachedColumns = isOpen
    ? getMasterDataRetailerMatchSourceColumnsCache(brandKey)
    : undefined;
  const sourceColumns = cachedColumns ?? localColumns;
  const isReady = cachedColumns != null || localReady;

  useEffect(() => {
    if (!isOpen) {
      setLocalColumns([]);
      setLocalReady(false);
      return;
    }

    if (getMasterDataRetailerMatchSourceColumnsCache(brandKey)) {
      return;
    }

    let cancelled = false;

    void loadMatchSourceColumns(brandKey)
      .then((columns) => {
        if (cancelled) return;
        setLocalColumns(columns);
        setLocalReady(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLocalColumns([]);
        setLocalReady(true);
        errorToastRef.current({
          content:
            err instanceof Error && err.message.trim()
              ? err.message
              : "Failed to load source columns",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [brandKey, isOpen]);

  return { sourceColumns, isReady };
}
